// app/career/[slug]/apply/ApplyForm.tsx
'use client';

import React, { useState, useEffect, useRef } from 'react';

import { useSearchParams, usePathname } from 'next/navigation';
import Link from 'next/link';
import { AlertCircle, ArrowLeft, Mic, PlayCircle, X, FileText, Copy, Check, Volume2, Loader2, Pause, RotateCcw, Mail, Lock, User, Briefcase, LogOut, ChevronRight, MapPin, Clock, CalendarClock } from 'lucide-react';
import { createBrowserSupabaseClient } from '@/lib/supabase-browser';
import { assertCareerUserAccess } from '@/lib/users-auth';
import {
  MONTHLY_APPLICATION_LIMIT_CODE,
  getUtcMonthWindow,
  isSameUtcMonth,
} from '@/lib/application-rules';

interface ApplyFormProps {
  jobTitle: string;
  slug: string;
}

const SKILLS_BY_CATEGORY: Record<string, string[]> = {
  'Virtual Assistant': ['Email handling', 'Customer support', 'CRM management', 'Online research', 'Data entry', 'Travel coordination', 'File & cloud management', 'Calendar Coordination', 'Document preparation'],
  'Sales Support': ['Lead generation', 'Inbound sales', 'B2B sales', 'B2C sales', 'Cold calling', 'Email support', 'Phone support (inbound/outbound)', 'Live chat support', 'Outbound sales'],
  // Customer Support skills — includes the combined sales + support skill set
  // for the "Customer Support & Sales Representative" role so the existing
  // category in the database can drive the form without any schema change.
  // (Original "Inbound sales" duplicate and "B2C SALES" typo de-duplicated.)
  'Customer Support': [
    // Customer support skills
    'Live chat',
    'Inbound customer support',
    'Conflict resolution',
    'Troubleshooting',
    'Help desk',
    // Sales / lead-gen skills (for the Customer Support & Sales Representative role)
    'Lead generation',
    'Inbound sales',
    'B2B sales',
    'B2C sales',
    'Cold calling',
    'Email support',
    'Phone support (inbound/outbound)',
    'Outbound sales',
  ],
  'Digital Marketing': ['SEO', 'Content creation', 'Meta ads', 'Graphic design', 'Social media management', 'Marketing analytics & reporting', 'Google ads', 'Copywriting'],
};

const VOICE_SCRIPT = `Good day. May I please speak with Mr. Craig Davis?\n\nHello Mr. Davis, my name is [Your Full Name], calling on behalf of Veridian Equity Partners.\n\nI'm reaching out to confirm you've received the corporate voting documentation we sent for the upcoming shareholder meeting scheduled for July 1st, 2024.\n\nIf you're unable to attend in person, I'd be happy to record your voting instructions over the phone. The board has recommended a vote IN FAVOR of the proposals — would you like to vote in alignment with their recommendation?\n\nWould you also like to cast your vote across all accounts held in your name?\n\n-For verification purposes, could you please confirm your full legal name?\n\n- And just to ensure our records are accurate — you currently reside at Lakewood Bend Blvd, Dallas, TX 75214 correct? If so, please state your full street address for confirmation.\n\n\nThank you — your vote will be confirmed in writing within 5 business days. If you have any questions, feel free to reach us toll-free at the number listed on your confirmation.\n\nMr. Davis — your participation matters, and we truly appreciate your time.\n\nHave a wonderful day.`;

const STEP_LABELS = ['Account', 'Education', 'Availability', 'Resume', 'Review'];
const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  reviewed: 'bg-blue-100 text-blue-800',
  accepted: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

export default function ApplyForm({ jobTitle, slug }: ApplyFormProps) {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const confirmed = searchParams?.get('confirmed') === 'true';

  const [submitted, setSubmitted] = useState<'' | 'submitted'>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fetchingCategory, setFetchingCategory] = useState(true);
  const [jobCategory, setJobCategory] = useState('');
  const [jobRequiresVoice, setJobRequiresVoice] = useState(false);
  const [jobSkills, setJobSkills] = useState<string[]>([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [accountStatus, setAccountStatus] = useState<'creating' | 'verify-email' | 'confirmed'>('creating');
  const [signupLoading, setSignupLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);

  const [sidebarView, setSidebarView] = useState<'apply' | 'account' | 'browse'>('apply');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');

  const [editLoading, setEditLoading] = useState(false);
  const [editSuccess, setEditSuccess] = useState('');

  // Lightweight toast notification. Used for non-blocking alerts that
  // should be more visible than the inline red error banner — e.g. when an
  // existing user tries to register and we want to nudge them to sign in.
  // `null` = no toast. Otherwise: { kind, message, action? }.
  const [toast, setToast] = useState<
    | null
    | {
        kind: 'info' | 'warning' | 'success' | 'error';
        message: string;
        actionLabel?: string;
        actionHref?: string;
      }
  >(null);
  // Auto-dismiss toasts after 8s. The user can also close them manually.
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 8000);
    return () => clearTimeout(t);
  }, [toast]);

  const [allJobs, setAllJobs] = useState<any[]>([]);
  const [userApplications, setUserApplications] = useState<any[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  // `alreadyApplied` is true when the logged-in user has already submitted
  // an application for THIS job (`slug`). We use it to render an
  // "Already applied" state instead of the editable form.
  const [alreadyApplied, setAlreadyApplied] = useState(false);
  const [alreadyAppliedInfo, setAlreadyAppliedInfo] = useState<any>(null);
  // `monthLimitedJobTitle` is set when the user already applied to a DIFFERENT
  // job during the current calendar month. Because only one application is
  // allowed per month, every other posting is locked until the 1st of next
  // month. It stays null when the user is free to apply.
  const [monthLimitedJobTitle, setMonthLimitedJobTitle] = useState<string | null>(null);

  const [recording, setRecording] = useState<Blob | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [playbackTime, setPlaybackTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioDuration, setAudioDuration] = useState(0);
  const [isAudioLoaded, setIsAudioLoaded] = useState(false);
  const [audioUrl, setAudioUrl] = useState('');
  const [showScript, setShowScript] = useState(false);
  const [copied, setCopied] = useState(false);
  const [recordedDuration, setRecordedDuration] = useState(0);
  const [scriptReadTime, setScriptReadTime] = useState(0);

  const [formData, setFormData] = useState({
    name: '', email: '', phoneCode: '+251', phone: '', password: '', gender: '', country: '', city: '', language: ['English'] as string[],
    highest_education: '', field_of_education: '', years_of_experience: '', currently_working: '',
    background_summary: '', skills: [] as string[],
    availability_fulltime: false, when_can_you_start: '', working_hours_acknowledged: false, anything_else: '',
    resume: null as File | null, portfolio_link: '',
  });

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const scriptTimerRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const recordingTimeRef = useRef<number>(0);
  const startTimeRef = useRef<number>(0);

  const requiresVoiceRecording = jobRequiresVoice;

  const availableSkills = jobSkills;
  const isLoggedIn = accountStatus === 'confirmed';

  // Defensive: language is sometimes returned from user_metadata as a string,
  // a JSON string, or null. Always normalize to a string[].
  const normalizeLanguages = (val: unknown): string[] => {
    if (Array.isArray(val)) return val.filter((x) => typeof x === 'string');
    if (typeof val === 'string') {
      const trimmed = val.trim();
      if (!trimmed) return [];
      if (trimmed.startsWith('[')) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) return parsed.filter((x) => typeof x === 'string');
        } catch {}
      }
      return [trimmed];
    }
    return [];
  };

  async function ensureUsersRow(name: string) {
    await fetch('/api/users/sync', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ name }),
    });
  }

  async function validateCareerSession(
    supabase: ReturnType<typeof createBrowserSupabaseClient>,
    userId: string,
    fallbackName: string
  ): Promise<boolean> {
    let access = await assertCareerUserAccess(supabase, userId);
    if (!access.allowed && access.missing) {
      await ensureUsersRow(fallbackName);
      access = await assertCareerUserAccess(supabase, userId);
    }
    if (!access.allowed) {
      await supabase.auth.signOut();
      setError(access.message);
      setAccountStatus('creating');
      return false;
    }
    return true;
  }

  function filterActiveJobs(jobs: any[]) {
    return jobs.filter((j) => j.is_active !== false && j.is_active !== 0);
  }

  async function fetchActiveJobs(): Promise<any[]> {
    const jobsRes = await fetch('/api/jobs', { cache: 'no-store' });
    if (!jobsRes.ok) {
      console.error('fetchActiveJobs failed:', jobsRes.status, await jobsRes.text());
      return [];
    }
    const jobsData = await jobsRes.json();
    return filterActiveJobs(jobsData.jobs || []);
  }

  async function fetchUserApplications(email: string): Promise<any[]> {
    if (!email) return [];
    try {
      const appsRes = await fetch(
        `/api/applications/user?email=${encodeURIComponent(email)}`,
        { cache: 'no-store' }
      );
      if (!appsRes.ok) {
        console.error('fetchUserApplications failed:', appsRes.status);
        return [];
      }
      const appsData = await appsRes.json();
      return appsData.applications || [];
    } catch (err) {
      console.error('fetchUserApplications:', err);
      return [];
    }
  }

  /**
   * Applies the "one application per calendar month" rule to a fetched list of
   * the user's applications.
   *
   * - Applied to THIS job at any time  -> the "Already applied" card handles it.
   * - Applied to ANOTHER job this month -> lock every other posting until the
   *   1st of next month (returns that other job's title).
   * - Otherwise -> free to apply (returns null).
   *
   * Returns the title of the job that consumed this month's allowance, or null.
   */
  function resolveMonthlyLock(apps: any[], currentJobId: number | null): string | null {
    const sameMonth = apps.filter(
      (a: any) => a.job_id != null && isSameUtcMonth(a.applied_at)
    );

    // If this month's allowance was already spent on the job being viewed,
    // `alreadyApplied` takes precedence and no extra lock is needed.
    const otherJobThisMonth = sameMonth.find(
      (a: any) => currentJobId == null || a.job_id !== currentJobId
    );

    if (!otherJobThisMonth) return null;

    return otherJobThisMonth.jobs?.title || 'another position';
  }

  async function checkExistingApplication(email: string) {
    try {
      const jobs = await fetchActiveJobs();
      const apps = await fetchUserApplications(email);
      const currentJob = jobs.find((j: any) => j.slug === slug);
      if (currentJob) {
        const existingApp = apps.find((a: any) => a.job_id === currentJob.id);
        if (existingApp) {
          setAlreadyApplied(true);
          setAlreadyAppliedInfo(existingApp);
          // Pre-populate the browse list as well so the sidebar's
          // "Browse Jobs" view can show statuses for every job.
          setAllJobs(jobs);
          setUserApplications(apps);
        }
      }

      // Lock the remaining postings if this month's single application was
      // already used on a different job.
      setMonthLimitedJobTitle(resolveMonthlyLock(apps, currentJob?.id ?? null));
    } catch (err) {
      console.error('checkExistingApplication:', err);
    }
  }

  useEffect(() => {
    async function checkSession() {
      setCheckingSession(true);
      try {
        const supabase = createBrowserSupabaseClient();
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          const meta = session.user.user_metadata || {};
          const displayName = meta.name || session.user.email?.split('@')[0] || '';
          if (!(await validateCareerSession(supabase, session.user.id, displayName))) return;
          setAccountStatus('confirmed');
          setCurrentStep(2);
          const email = session.user.email || '';
          setUserEmail(email);
          setUserName(displayName);
          setFormData(prev => ({
            ...prev, name: meta.name || prev.name || '', email: session.user.email || prev.email,
            phone: meta.phone || prev.phone, phoneCode: meta.phoneCode || prev.phoneCode, gender: meta.gender || prev.gender,
            country: meta.country || prev.country, city: meta.city || prev.city,
            language: normalizeLanguages(meta.language).length > 0 ? normalizeLanguages(meta.language) : prev.language,
          }));
          await checkExistingApplication(email);
        } else if (confirmed) {
          await new Promise(r => setTimeout(r, 1500));
          const { data: { session: rs } } = await supabase.auth.getSession();
          if (rs) {
            const meta = rs.user.user_metadata || {};
            const displayName = meta.name || rs.user.email?.split('@')[0] || '';
            if (!(await validateCareerSession(supabase, rs.user.id, displayName))) return;
            setAccountStatus('confirmed'); setCurrentStep(2);
            const email = rs.user.email || '';
            setUserEmail(email);
            setUserName(displayName);
            setFormData(prev => ({
              ...prev, name: meta.name || prev.name || '', email: rs.user.email || prev.email,
              phone: meta.phone || prev.phone, phoneCode: meta.phoneCode || prev.phoneCode, gender: meta.gender || prev.gender,
              country: meta.country || prev.country, city: meta.city || prev.city,
              language: normalizeLanguages(meta.language).length > 0 ? normalizeLanguages(meta.language) : prev.language,
            }));
            await checkExistingApplication(email);
          } else setAccountStatus('creating');
        } else setAccountStatus('creating');
      } catch { setAccountStatus('creating'); }
      finally { setCheckingSession(false); }
    }
    checkSession();
  }, [confirmed]);

  useEffect(() => {
    async function fetchJobCategory() {
      try { setFetchingCategory(true); const res = await fetch(`/api/jobs/slug/${slug}`); const data = await res.json(); if (data.job?.category) setJobCategory(data.job.category); if (typeof data.job?.voice === 'boolean') setJobRequiresVoice(data.job.voice); if (typeof data.job?.skills === 'string') { const parsed = data.job.skills.split(',').map((s: string) => s.trim()).filter(Boolean); setJobSkills(parsed); } }
      catch { } finally { setFetchingCategory(false); }
    }
    fetchJobCategory();
  }, [slug]);

  useEffect(() => {
    if (showScript) scriptTimerRef.current = setInterval(() => setScriptReadTime(p => p + 1), 1000);
    else { if (scriptTimerRef.current) clearInterval(scriptTimerRef.current); setScriptReadTime(0); }
    return () => { if (scriptTimerRef.current) clearInterval(scriptTimerRef.current); };
  }, [showScript]);

  useEffect(() => {
    const a = audioRef.current; if (!a) return;
    const ol = () => { if (a.duration && isFinite(a.duration)) { setAudioDuration(a.duration); setIsAudioLoaded(true); if (recordedDuration === 0 && a.duration > 0) setRecordedDuration(Math.floor(a.duration)); } };
    const ot = () => setPlaybackTime(a.currentTime);
    const op = () => setIsPlaying(true); const opl = () => setIsPlaying(false);
    const oe = () => { setIsPlaying(false); setPlaybackTime(0); }; const oer = () => setIsAudioLoaded(false);
    a.addEventListener('loadedmetadata', ol); a.addEventListener('timeupdate', ot);
    a.addEventListener('play', op); a.addEventListener('pause', opl);
    a.addEventListener('ended', oe); a.addEventListener('error', oer);
    if (a.src && !isAudioLoaded) a.load();
    return () => { a.removeEventListener('loadedmetadata', ol); a.removeEventListener('timeupdate', ot); a.removeEventListener('play', op); a.removeEventListener('pause', opl); a.removeEventListener('ended', oe); a.removeEventListener('error', oer); };
  }, [audioUrl, isAudioLoaded, recordedDuration]);

  useEffect(() => { if (recording) { setIsAudioLoaded(false); setAudioDuration(0); setPlaybackTime(0); setIsPlaying(false); } }, [recording]);

  const startRecording = async () => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') mediaRecorderRef.current.stop();
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
      audioChunksRef.current = [];
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 44100 } });
      streamRef.current = stream;
      let r: MediaRecorder; try { r = new MediaRecorder(stream, { mimeType: 'audio/webm' }); } catch { r = new MediaRecorder(stream); }
      r.ondataavailable = e => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      r.onstop = () => {
        if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
        setRecordedDuration(recordingTimeRef.current);
        const blob = new Blob(audioChunksRef.current, { type: audioChunksRef.current[0]?.type || 'audio/webm' });
        setRecording(blob); setAudioUrl(URL.createObjectURL(blob));
        if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
        setIsRecording(false); setRecordingTime(0); recordingTimeRef.current = 0; startTimeRef.current = 0;
      };
      r.start(100); mediaRecorderRef.current = r; setIsRecording(true);
      startTimeRef.current = Date.now(); recordingTimeRef.current = 0; setRecordingTime(0); setRecordedDuration(0); audioChunksRef.current = [];
      timerRef.current = setInterval(() => {
        const el = Math.min(Math.floor((Date.now() - startTimeRef.current) / 1000), 300);
        setRecordingTime(el); recordingTimeRef.current = el; if (el >= 300) stopRecording();
      }, 1000);
    } catch { setError('Please allow microphone access to record your voice.'); setIsRecording(false); }
  };
  const stopRecording = () => { if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') mediaRecorderRef.current.stop(); else { setIsRecording(false); if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } } };
  const clearRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') mediaRecorderRef.current.stop();
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; setIsPlaying(false); setPlaybackTime(0); }
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setRecording(null); setAudioUrl(''); setIsRecording(false); setRecordingTime(0); recordingTimeRef.current = 0; startTimeRef.current = 0;
    setPlaybackTime(0); setAudioDuration(0); setRecordedDuration(0); setIsAudioLoaded(false); audioChunksRef.current = [];
  };
  const handleReRecord = () => { clearRecording(); setTimeout(startRecording, 100); };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, files } = e.target as any;
    if (name === 'resume' && files && files[0]) setFormData(p => ({ ...p, resume: files[0] })); else setFormData(p => ({ ...p, [name]: value }));
  };
  const handleSkillToggle = (skill: string) => setFormData(p => ({ ...p, skills: p.skills.includes(skill) ? p.skills.filter(s => s !== skill) : [...p.skills, skill] }));
  const handleLanguageToggle = (lang: string) => setFormData(p => {
    const current = normalizeLanguages(p.language);
    return { ...p, language: current.includes(lang) ? current.filter(l => l !== lang) : [...current, lang] };
  });
  const safeLanguage = normalizeLanguages(formData.language);
  const fmtTime = (s: number) => { if (!isFinite(s) || isNaN(s)) return '00:00'; return `${Math.floor(s / 60).toString().padStart(2, '0')}:${Math.floor(s % 60).toString().padStart(2, '0')}`; };
  const calcProg = (c: number, t: number) => (t <= 0 || !isFinite(t)) ? 0 : (c / t) * 100;
  const getDur = () => recordedDuration > 0 ? recordedDuration : (isAudioLoaded && audioDuration > 0 ? audioDuration : 0);
  const copyScript = async () => { try { await navigator.clipboard.writeText(VOICE_SCRIPT); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { setError('Failed to copy script'); } };
  const handlePlayPause = () => { if (!audioRef.current) return; if (isPlaying) audioRef.current.pause(); else { if (!isAudioLoaded) audioRef.current.load(); audioRef.current.play().catch(() => setError('Error playing audio')); } };
  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => { const t = parseFloat(e.target.value); setPlaybackTime(t); if (audioRef.current) audioRef.current.currentTime = t; };
  const countWords = (t: string) => t.trim().split(/\s+/).filter(Boolean).length;

  const validateStep2 = () => {
    if (!formData.highest_education) { setError('Please select your highest level of education'); return false; }
    if (!formData.field_of_education) { setError('Please select your field of education'); return false; }
    if (!formData.years_of_experience) { setError('Please select your years of experience'); return false; }
    if (!formData.currently_working) { setError('Please select your current work status'); return false; }
    if (countWords(formData.background_summary) < 50) { setError('Background summary must be at least 50 words (currently ' + countWords(formData.background_summary) + ')'); return false; }
    if (availableSkills.length > 0 && formData.skills.length === 0) { setError('Please select at least one skill'); return false; }
    setError(''); return true;
  };
  const validateStep3 = () => {
    if (requiresVoiceRecording && !recording) { setError('Voice recording is required for this position'); return false; }
    if (!formData.when_can_you_start) { setError('Please select when you can start'); return false; }
    if (!formData.working_hours_acknowledged) { setError('You must acknowledge the working hours to proceed'); return false; }
    setError(''); return true;
  };

  const handleCreateAccount = async () => {
    setError(''); setSignupLoading(true);
    if (!formData.name.trim()) { setError('Please enter your full name'); setSignupLoading(false); return; }
    if (!formData.email.trim()) { setError('Please enter your email address'); setSignupLoading(false); return; }
    if (!formData.password || formData.password.length < 6) { setError('Password must be at least 6 characters'); setSignupLoading(false); return; }
    if (!formData.gender) { setError('Please select your gender'); setSignupLoading(false); return; }
    if (!formData.country.trim()) { setError('Please enter your country'); setSignupLoading(false); return; }
    if (!formData.city.trim()) { setError('Please enter your city'); setSignupLoading(false); return; }
    if (!formData.phone.trim()) { setError('Please enter your phone number'); setSignupLoading(false); return; }
    try {
      const supabase = createBrowserSupabaseClient();
      // We intentionally do NOT pass `emailRedirectTo`. With email
      // confirmation disabled in the Supabase project, the user is signed
      // in immediately and a `redirect_to` is both unnecessary and a
      // common source of `400 Bad Request` from the auth API (it has to
      // match the project's "Additional Redirect URLs" allow-list). If
      // email confirmation is re-enabled later, add it back here AND
      // whitelist the URL in Supabase Auth → URL Configuration.
      const { data, error: e } = await supabase.auth.signUp({
        email: formData.email.trim(),
        password: formData.password,
        options: {
          data: {
            name: formData.name.trim(),
            gender: formData.gender,
            country: formData.country.trim(),
            city: formData.city.trim(),
            phone: `${formData.phoneCode.trim()}${formData.phone.trim()}`,
            phoneCode: formData.phoneCode.trim(),
            language: formData.language,
          },
        },
      });
      if (e) throw e;
      if (data.user && data.session) {
        await ensureUsersRow(formData.name.trim());
        const access = await assertCareerUserAccess(supabase, data.user.id);
        if (!access.allowed) {
          await supabase.auth.signOut();
          throw new Error(access.message);
        }
      }
      if (data.user && !data.session) setAccountStatus('verify-email');
      else if (data.session) { setAccountStatus('confirmed'); setCurrentStep(2); setUserEmail(data.user?.email || formData.email); setUserName(formData.name); }
    } catch (err) {
      // Log the full error object so we can see the real Supabase error
      // message in DevTools — the browser console only shows the 400 status
      // by default, but the response body contains the actual reason.
      // eslint-disable-next-line no-console
      console.error('Supabase signUp failed:', err);
      const m = err instanceof Error ? err.message : 'Failed to create account.';
      const lower = m.toLowerCase();
      if (lower.includes('already') || lower.includes('exists')) {
        // Existing account — promote this to a high-visibility toast with a
        // direct "Sign in" CTA so the user knows exactly what to do next.
        // We also pre-fill `email` in the login URL so they don't have to
        // retype it.
        setToast({
          kind: 'info',
          message: `An account already exists for ${formData.email.trim()}. Please sign in to continue your application.`,
          actionLabel: 'Sign in',
          actionHref: `/auth/login?email=${encodeURIComponent(formData.email.trim())}&redirect=${encodeURIComponent(pathname + '?confirmed=true')}`,
        });
        setError('An account with this email already exists. Please sign in instead.');
      } else if (lower.includes('signups are disabled') || lower.includes('signups disabled')) {
        // This is a Supabase project setting, not a user error. Surface a
        // clear admin-facing message that points to the exact toggle that
        // needs to be flipped in the Supabase dashboard.
        setError(
          'Email signups are currently disabled on the site. ' +
          'An admin needs to enable them in Supabase: ' +
          'Authentication \u2192 Providers \u2192 Email \u2192 "Enable email signups". ' +
          'Please contact the site administrator.'
        );
      } else if (lower.includes('password') || lower.includes('weak')) {
        setError('Password is too weak. Please use at least 6 characters with a mix of letters and numbers.');
      } else if (lower.includes('rate') || lower.includes('limit')) {
        setError('Too many attempts. Please wait a moment and try again.');
      } else if (lower.includes('email') && (lower.includes('invalid') || lower.includes('format'))) {
        setError('Please enter a valid email address.');
      } else {
        setError(m || 'Failed to create account. Please try again.');
      }
    } finally { setSignupLoading(false); }
  };

  const goToNextStep = () => {
    if (currentStep === 2 && !validateStep2()) return;
    if (currentStep === 3 && !validateStep3()) return;
    setError(''); setCurrentStep(currentStep + 1);
  };

  const handleSubmit = async () => {
    setLoading(true); setError('');
    if (!formData.resume || formData.resume.size === 0) { setError('Please upload your resume'); setLoading(false); return; }
    if (formData.resume.size > 5 * 1024 * 1024) { setError('Resume must be less than 5MB'); setLoading(false); return; }
    const ext = formData.resume.name.toLowerCase().slice(formData.resume.name.lastIndexOf('.'));
    if (!['.pdf', '.doc', '.docx'].includes(ext)) { setError('Please upload a PDF, DOC, or DOCX file'); setLoading(false); return; }
    try {
      const fd = new FormData();
      fd.append('job_slug', slug); fd.append('name', formData.name); fd.append('email', formData.email); fd.append('phone', `${formData.phoneCode}${formData.phone}`);
      fd.append('job_title', jobTitle); fd.append('job_category', jobCategory);
      fd.append('highest_education', formData.highest_education); fd.append('field_of_education', formData.field_of_education);
      fd.append('years_of_experience', formData.years_of_experience); fd.append('currently_working', formData.currently_working);
      fd.append('background_summary', formData.background_summary); fd.append('skills', JSON.stringify(formData.skills));
      fd.append('availability_fulltime', String(formData.availability_fulltime)); fd.append('working_hours_acknowledged', String(formData.working_hours_acknowledged)); fd.append('when_can_you_start', formData.when_can_you_start);
      fd.append('anything_else', formData.anything_else);
      if (formData.resume) fd.append('resume', formData.resume);
      if (formData.portfolio_link) fd.append('portfolio', formData.portfolio_link);
      if (recording) fd.append('voice_recording', new File([recording], `voice-${Date.now()}.webm`, { type: 'audio/webm' }));
      const res = await fetch('/api/applications', { method: 'POST', body: fd });
      const result = await res.json();

      // The API is the source of truth for the one-per-month rule. If the user
      // slipped past the UI lock (stale tab, back button, direct request),
      // switch straight into the locked state instead of showing a raw error.
      if (res.status === 429 && result.code === MONTHLY_APPLICATION_LIMIT_CODE) {
        setMonthLimitedJobTitle(result.previousApplication?.jobTitle || 'another position');
        throw new Error(result.error || 'You can submit only one job application per month.');
      }

      if (!res.ok) throw new Error(result.error || result.details || 'Failed to submit');
      // Edit-application flow has been removed: every successful submit is a
      // brand new application. Mark as already-applied so the UI switches to
      // the "Already applied" state and the success screen renders.
      setAlreadyApplied(true);
      if (result.application) setAlreadyAppliedInfo(result.application);
      setSubmitted('submitted');
    } catch (err) { setError(err instanceof Error ? err.message : 'Something went wrong'); } finally { setLoading(false); }
  };


  const handleUpdateAccount = async () => {
    setEditLoading(true); setError(''); setEditSuccess('');
    try {
      const supabase = createBrowserSupabaseClient();
      const { error: e } = await supabase.auth.updateUser({
        data: { name: formData.name, gender: formData.gender, country: formData.country, city: formData.city, phone: `${formData.phoneCode}${formData.phone}`, phoneCode: formData.phoneCode, language: formData.language },
      });
      if (e) throw e;
      setUserName(formData.name);
      setEditSuccess('Profile updated successfully!');
      setTimeout(() => setEditSuccess(''), 3000);
    } catch (err) { setError(err instanceof Error ? err.message : 'Failed to update profile'); }
    finally { setEditLoading(false); }
  };

  const loadBrowseData = async () => {
    setLoadingJobs(true);
    try {
      let email = userEmail;
      if (!email) {
        const supabase = createBrowserSupabaseClient();
        const { data: { session } } = await supabase.auth.getSession();
        email = session?.user?.email || '';
        if (email) setUserEmail(email);
      }

      const jobs = await fetchActiveJobs();
      setAllJobs(jobs);

      const apps = await fetchUserApplications(email);
      setUserApplications(apps);

      const currentJob = jobs.find((j: any) => j.slug === slug);
      if (currentJob) {
        const existingApp = apps.find((a: any) => a.job_id === currentJob.id);
        if (existingApp) {
          setAlreadyApplied(true);
          setAlreadyAppliedInfo(existingApp);
        }
      }

      // Lock the remaining postings if this month's single application was
      // already used on a different job.
      setMonthLimitedJobTitle(resolveMonthlyLock(apps, currentJob?.id ?? null));

      if (jobs.length === 0) {
        console.warn('Browse jobs: /api/jobs returned no active jobs');
      }
    } catch (err) {
      console.error('loadBrowseData failed:', err);
      setError('Could not load jobs. Please refresh and try again.');
    } finally {
      setLoadingJobs(false);
    }
  };

  const openBrowseJobs = () => {
    setSidebarView('browse');
    setError('');
    void loadBrowseData();
  };

  useEffect(() => {
    if (sidebarView === 'browse' && isLoggedIn) void loadBrowseData();
  }, [sidebarView, isLoggedIn]);

  const handleLogout = async () => {
    const supabase = createBrowserSupabaseClient();
    await supabase.auth.signOut();
    window.location.href = `/career/${slug}`;
  };

  useEffect(() => () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    if (timerRef.current) clearInterval(timerRef.current);
    if (scriptTimerRef.current) clearInterval(scriptTimerRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') mediaRecorderRef.current.stop();
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
  }, [audioUrl]);

  // The "edit application" flow has been removed. Logged-in users who have
  // already applied for this job now see an "Already applied" card instead of
  // the editable form (see the `applyContent` wrapper below), so no prefilling
  // of the form state from `alreadyAppliedInfo` is needed any more.

  const alreadyAppliedCard = (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
      <div className="text-center max-w-xl mx-auto py-8">
        <div className="h-20 w-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <AlertCircle className="h-10 w-10 text-blue-600" />
        </div>
        <h2 className="text-2xl font-bold text-[#002F4A] mb-3">You have already applied</h2>
        <p className="text-gray-600 mb-2">
          You submitted an application for <strong>{jobTitle}</strong>.
        </p>
        {alreadyAppliedInfo?.status && (
          <p className="text-gray-700 mb-1">
            Current status:{' '}
            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium align-middle ${STATUS_COLORS[alreadyAppliedInfo.status] || 'bg-gray-100 text-gray-800'}`}>
              {alreadyAppliedInfo.status}
            </span>
          </p>
        )}
        {alreadyAppliedInfo?.applied_at && (
          <p className="text-sm text-gray-500 mb-6">
            Applied on{' '}
            {new Date(alreadyAppliedInfo.applied_at).toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })}
          </p>
        )}
        <p className="text-gray-600 mb-8">
          Our HR team will review your application and contact you if your profile matches.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            type="button"
            onClick={openBrowseJobs}
            className="bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-8 rounded-lg"
          >
            Browse More Jobs
          </button>
          <Link
            href={`/career/${slug}`}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-8 rounded-lg text-center"
          >
            Back to Job Details
          </Link>
        </div>
      </div>
    </div>
  );

  // Human-readable "applications reopen on …" label. Derived from the same UTC
  // window the API uses, so the date shown here matches the enforced one.
  const monthReopenLabel = new Date(getUtcMonthWindow().endIso).toLocaleDateString(
    'en-US',
    { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' }
  );

  // Shown when the user already spent this month's single application on a
  // DIFFERENT job. The form is replaced entirely so they cannot submit twice
  // in the same calendar month; applications reopen on the 1st of next month.
  const monthLockedCard = (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
      <div className="text-center max-w-xl mx-auto py-8">
        <div className="h-20 w-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CalendarClock className="h-10 w-10 text-amber-600" />
        </div>
        <h2 className="text-2xl font-bold text-[#002F4A] mb-3">
          One application per month
        </h2>
        <p className="text-gray-600 mb-4">
          You already applied for <strong>{monthLimitedJobTitle}</strong> this month.
          To keep our hiring process fair, each candidate can submit{' '}
          <strong>one application per calendar month</strong>.
        </p>
        <p className="text-gray-600 mb-6">
          You can apply for another position, including{' '}
          <strong>{jobTitle}</strong>, from{' '}
          <strong>{monthReopenLabel}</strong>.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            type="button"
            onClick={openBrowseJobs}
            className="bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-8 rounded-lg"
          >
            Browse Jobs
          </button>
          <Link
            href={`/career/${slug}`}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-8 rounded-lg text-center"
          >
            Back to Job Details
          </Link>
        </div>
      </div>
    </div>
  );

  const applyContent = (
    <>
      {sidebarView !== 'apply' || !isLoggedIn ? (
        <>
          <Link href={`/career/${slug}`} className="flex items-center text-indigo-600 mb-6 hover:text-orange-500"><ArrowLeft size={18} className="mr-2" /> Back to Job Details</Link>
          <h2 className="text-2xl font-semibold text-[#002F4A] mb-2">Apply for {jobTitle}</h2>
          <p className="text-gray-500 text-sm mb-6">Position: <span className="font-medium text-gray-700">{jobTitle}</span> &middot; Category: <span className="font-medium text-gray-700">{jobCategory}</span></p>
        </>
      ) : (
        <h2 className="text-xl font-bold text-[#002F4A] mb-4">Apply for {jobTitle}</h2>
      )}

      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          {STEP_LABELS.map((label, i) => {
            const step = i + 1;
            return (
              <div key={step} className="flex flex-col items-center flex-1">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${currentStep >= step ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-500'}`}>{currentStep > step ? '✓' : step}</div>
                <span className={`text-[10px] sm:text-xs mt-1 font-medium text-center ${currentStep >= step ? 'text-orange-600' : 'text-gray-400'}`}>{label}</span>
              </div>
            );
          })}
        </div>
        <div className="h-1 bg-gray-200 rounded-full overflow-hidden"><div className="h-full bg-orange-500 transition-all duration-300" style={{ width: `${(currentStep / 5) * 100}%` }} /></div>
      </div>

      {currentStep === 1 && accountStatus === 'creating' && (
        <div className="space-y-5">
          <div className="bg-blue-50 p-4 border-l-4 border-blue-500 rounded-r-lg mb-4"><div className="flex items-start"><User className="h-5 w-5 text-blue-600 mr-2 mt-0.5" /><div className="flex-1"><h3 className="font-bold text-blue-800">Create Your Account</h3><p className="text-blue-700 text-sm mt-1">Create an account to start your application.</p></div></div></div>
          <div><label className="block text-gray-700 font-medium mb-2">Full Name *</label><input type="text" name="name" required minLength={2} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" placeholder="Enter your full name" value={formData.name} onChange={handleInputChange} /></div>
          <div><label className="block text-gray-700 font-medium mb-2">Email Address *</label><input type="email" name="email" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" placeholder="Enter your email" value={formData.email} onChange={handleInputChange} /></div>
          <div><label className="block text-gray-700 font-medium mb-2">Password *</label><input type="password" name="password" required minLength={6} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" placeholder="Min. 6 characters" value={formData.password} onChange={handleInputChange} /></div>
          <div><label className="block text-gray-700 font-medium mb-2">Gender *</label><select name="gender" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" value={formData.gender} onChange={handleInputChange}><option value="">Select gender</option><option value="male">Male</option><option value="female">Female</option></select></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className="block text-gray-700 font-medium mb-2">Country *</label><select name="country" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" value={formData.country} onChange={handleInputChange} ><option value="">Select Country</option><option value="ethiopia">Ethiopia</option></select></div>
            <div><label className="block text-gray-700 font-medium mb-2">City *</label><input type="text" name="city" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" placeholder="City" value={formData.city} onChange={handleInputChange} /></div>
          </div>
          <div><label className="block text-gray-700 font-medium mb-2">Phone Number *</label>
            <div className="flex gap-2">
            <input 
  type="text" 
  name="phoneCode" 
  required 
  readOnly
  className="w-28 px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-100 text-gray-500 cursor-not-allowed" 
  placeholder="+251" 
  value={formData.phoneCode} 
  onChange={handleInputChange} 
/>              <input type="tel" name="phone" required className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white text-gray-900 placeholder-gray-500" placeholder="Phone number" value={formData.phone} onChange={handleInputChange} />
            </div>
          </div>
          <div><label className="block text-gray-700 font-medium mb-2">Preferred Languages * <span className="text-sm font-normal text-gray-500">(Select all that apply)</span></label>
            <div className="grid grid-cols-3 gap-2">
              {['English', 'Amharic', 'Arabic'].map(lang => (
                <label key={lang} className={`flex items-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${safeLanguage.includes(lang) ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-200 hover:border-gray-300'}`}>
                  <input type="checkbox" checked={safeLanguage.includes(lang)} onChange={() => handleLanguageToggle(lang)} className="w-4 h-4 text-orange-500 rounded" />
                  <span className="text-sm">{lang}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex flex-col items-end gap-3 pt-4">
            <button type="button" onClick={handleCreateAccount} disabled={signupLoading} className="bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-8 rounded-lg disabled:opacity-50 flex items-center gap-2">{signupLoading ? <><Loader2 className="h-5 w-5 animate-spin" /> Creating...</> : <><Lock className="h-5 w-5" /> Create Account</>}</button>
            <Link href={`/auth/login?redirect=${encodeURIComponent(pathname + '?confirmed=true')}`} className="text-sm text-indigo-600 hover:text-orange-500 font-medium">Already have an account? Sign in</Link>
          </div>
        </div>
      )}

      {currentStep === 2 && (
        <div className="space-y-5">
          <div><label className="block text-gray-700 font-medium mb-2">Highest Level of Education *</label><select name="highest_education" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" value={formData.highest_education} onChange={handleInputChange}><option value="">Select</option><option value="bachelor">Bachelor{"'"}s Degree</option><option value="master">Master{"'"}s Degree</option><option value="phd">PhD</option></select></div>
          <div><label className="block text-gray-700 font-medium mb-2">Field of Education *</label><select name="field_of_education" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" value={formData.field_of_education} onChange={handleInputChange}><option value="">Select field</option><option value="accounting">Accounting</option><option value="business_administration">Business Administration</option><option value="marketing_communication">Marketing or Communication</option><option value="computer_science">Computer Science</option><option value="human_resource">Human Resource</option><option value="health_science">Health Science / Medical Field</option><option value="engineering">Engineering</option><option value="psychology">Psychology or Social Work</option></select></div>
          <div><label className="block text-gray-700 font-medium mb-2">Years of Experience *</label><select name="years_of_experience" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" value={formData.years_of_experience} onChange={handleInputChange}><option value="">Select</option><option value="0-1">0 - 1 years</option><option value="1-3">1 - 3 years</option><option value="3-5">3 - 5 years</option><option value="5+">5+ years</option></select></div>
          <div><label className="block text-gray-700 font-medium mb-2">Are You Currently Working? *</label><select name="currently_working" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" value={formData.currently_working} onChange={handleInputChange}><option value="">Select</option><option value="employed">Employed</option><option value="freelance">Freelance</option><option value="not_working">Not Working</option></select></div>
          {availableSkills.length > 0 && <div><label className="block text-gray-700 font-medium mb-2">Skills * <span className="text-sm font-normal text-gray-500">(Select all that apply)</span></label><div className="grid grid-cols-2 gap-2">{availableSkills.map(s => <label key={s} className={`flex items-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${formData.skills.includes(s) ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'}`}><input type="checkbox" checked={formData.skills.includes(s)} onChange={() => handleSkillToggle(s)} className="w-4 h-4 text-orange-500 rounded" /><span className="text-sm">{s}</span></label>)}</div></div>}
          <div><label className="block text-gray-700 font-medium mb-2">Background Summary * <span className="text-sm font-normal text-gray-500">(50+ words)</span></label><textarea name="background_summary" required rows={5} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" placeholder="Tell us about your background..." value={formData.background_summary} onChange={handleInputChange} /><p className={`text-sm mt-1 ${countWords(formData.background_summary) < 50 ? 'text-red-500' : 'text-green-600'}`}>{countWords(formData.background_summary)} / 50 words</p></div>
          <div className="flex justify-end pt-4">
            <button type="button" onClick={goToNextStep} className="bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-8 rounded-lg">Next →</button>
          </div>
        </div>
      )}

      {currentStep === 3 && (
        <div className="space-y-6">
          {requiresVoiceRecording && (
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 border-l-4 border-blue-500 rounded-r-lg"><div className="flex items-start"><Volume2 className="h-5 w-5 text-blue-600 mr-2 mt-0.5" /><div className="flex-1"><h3 className="font-bold text-blue-800">Voice Assessment Required</h3><p className="text-blue-700 text-sm mt-1">Read the provided script in a professional, clear tone.</p></div></div></div>
              <div className="flex gap-3"><button type="button" onClick={() => setShowScript(!showScript)} className="flex items-center gap-2 px-4 py-3 bg-white border border-blue-500 text-blue-600 hover:bg-blue-50 rounded-lg font-medium"><FileText size={20} />{showScript ? 'Hide Script' : 'View Script'}</button><button type="button" onClick={copyScript} className="flex items-center gap-2 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium">{copied ? <Check size={20} className="text-green-600" /> : <Copy size={20} />}{copied ? 'Copied!' : 'Copy Script'}</button></div>
              {showScript && <div className="bg-gray-50 border border-gray-200 rounded-xl p-6"><h4 className="font-semibold text-gray-900 mb-3">Phone Script</h4><div className="bg-white p-5 rounded-lg border border-gray-100 max-h-60 overflow-y-auto text-gray-700 leading-relaxed whitespace-pre-line text-sm">{VOICE_SCRIPT}</div></div>}
              <div className="border border-gray-300 rounded-lg p-4 bg-white shadow-sm">
                <label className="block text-gray-700 font-medium mb-4">Voice Recording *</label>
                {!recording ? (
                  <div className="space-y-3">
                    <button type="button" onClick={isRecording ? stopRecording : startRecording} className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium ${isRecording ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-orange-500 hover:bg-orange-600 text-white'}`}><Mic size={20} />{isRecording ? 'Stop Recording' : 'Start Recording'}</button>
                    {isRecording && <div className="flex items-center gap-2 text-sm"><div className="h-2 w-2 bg-red-600 rounded-full animate-pulse" /><span className="text-red-600 font-medium">Recording: {fmtTime(recordingTime)}</span><span className="text-gray-500">(Max: 5:00)</span></div>}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3"><PlayCircle className="text-green-600" size={24} /><span className="font-medium">Recording complete ✓ — {fmtTime(getDur())}</span><button type="button" onClick={clearRecording} className="text-red-600 hover:text-red-700 ml-auto" aria-label="Clear recording"><X size={20} /></button></div>
                    <div className="bg-gray-50 p-3 rounded-lg"><audio ref={audioRef} src={audioUrl} preload="metadata" className="hidden" /><div className="flex items-center gap-3"><button type="button" onClick={handlePlayPause} className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center" aria-label={isPlaying ? 'Pause' : 'Play'}>{isPlaying ? <Pause size={16} /> : <PlayCircle size={16} />}</button><input type="range" min="0" max={getDur() || 0} value={playbackTime} onChange={handleSeek} className="flex-1 h-2 rounded-lg appearance-none bg-gray-200 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-orange-500" /></div></div>
                    <div className="flex gap-3"><button type="button" onClick={handleReRecord} className="flex-1 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg flex items-center justify-center gap-2"><Mic size={16} /> Re-record</button><button type="button" onClick={clearRecording} className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-lg">Remove</button></div>
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="space-y-5 border-t border-gray-200 pt-6">
            <h3 className="text-lg font-semibold text-[#002F4A]">Availability & Preferences</h3>
            <label className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50"><input type="checkbox" checked={formData.availability_fulltime} onChange={e => setFormData(p => ({ ...p, availability_fulltime: e.target.checked }))} className="w-5 h-5 text-orange-500 rounded" /><span className="text-gray-700 font-medium">Full-time Availability</span></label>
            <div><label className="block text-gray-700 font-medium mb-2">When Can You Start? *</label><select name="when_can_you_start" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" value={formData.when_can_you_start} onChange={handleInputChange}><option value="">Select</option><option value="immediately">Immediately</option><option value="2_weeks">In 2 weeks</option><option value="1_month">In 1 month</option></select></div>
            <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-lg p-4"><p className="text-amber-800 text-sm font-medium">Working hours: 3:30 PM - 2:00 AM (9:30 AM local - 8:00 AM local). You must be available.</p></div>
            <label className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50"><input type="checkbox" checked={formData.working_hours_acknowledged} onChange={e => setFormData(p => ({ ...p, working_hours_acknowledged: e.target.checked }))} className="w-5 h-5 text-orange-500 rounded" /><span className="text-gray-700 font-medium">I understand and am available for these hours *</span></label>
            <div><label className="block text-gray-700 font-medium mb-2">Anything else? <span className="text-sm font-normal text-gray-500">(Optional)</span></label><textarea name="anything_else" rows={3} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" placeholder="Additional info..." value={formData.anything_else} onChange={handleInputChange} /></div>
          </div>
          <div className="flex justify-between pt-4">
            <button type="button" onClick={() => setCurrentStep(2)} className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-3 px-8 rounded-lg">← Back</button>
            <button type="button" onClick={goToNextStep} className="bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-8 rounded-lg">Next →</button>
          </div>
        </div>
      )}

      {currentStep === 4 && (
        <div className="space-y-6">
          <div className="border border-gray-200 rounded-xl p-6 bg-white shadow-sm">
            <label className="block text-gray-700 font-medium mb-2">Upload Resume (PDF, DOC, DOCX) *</label>
            <input type="file" name="resume" accept=".pdf,.doc,.docx" required={!formData.resume} className="w-full border border-gray-300 rounded-lg p-3 cursor-pointer focus:outline-none focus:ring-2 focus:ring-orange-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-orange-100 file:text-orange-700 file:cursor-pointer" onChange={handleInputChange} />
            {formData.resume && <p className="mt-2 text-sm text-green-600">✓ {formData.resume.name}</p>}
            <p className="mt-2 text-sm text-gray-500">Max 5MB. PDF, DOC, DOCX.</p>
          </div>
          <div className="border border-gray-200 rounded-xl p-6 bg-white shadow-sm">
            <label className="block text-gray-700 font-medium mb-2">Portfolio / LinkedIn <span className="text-sm font-normal text-gray-500">(Optional)</span></label>
            <input type="url" name="portfolio_link" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" placeholder="https://..." value={formData.portfolio_link} onChange={handleInputChange} />
          </div>
          <div className="flex justify-between pt-4">
            <button type="button" onClick={() => setCurrentStep(3)} className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-3 px-8 rounded-lg">← Back</button>
            <button type="button" onClick={goToNextStep} className="bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-8 rounded-lg">Review →</button>
          </div>
        </div>
      )}

      {currentStep === 5 && (
        <div className="space-y-6">
          <h3 className="text-lg font-semibold text-[#002F4A]">Review Your Application</h3>
          <div className="bg-gray-50 rounded-xl p-6 space-y-3 border border-gray-200">
            <RR l="Name" v={formData.name} /><RR l="Email" v={formData.email} /><RR l="Phone" v={formData.phone} /><RR l="Location" v={`${formData.city}, ${formData.country}`} /><RR l="Language" v={safeLanguage.join(', ') || '—'} />
            <hr className="border-gray-200" />
            <RR l="Education" v={formData.highest_education} /><RR l="Field" v={formData.field_of_education} /><RR l="Experience" v={formData.years_of_experience} /><RR l="Work Status" v={formData.currently_working} />
            {formData.skills.length > 0 && <RR l="Skills" v={formData.skills.join(', ')} />}
            <RR l="Background" v={formData.background_summary} />
            <hr className="border-gray-200" />
            <RR l="Availability" v={formData.availability_fulltime ? 'Full-time' : 'Not specified'} /><RR l="Start Date" v={formData.when_can_you_start} /><RR l="Resume" v={formData.resume?.name || 'Not uploaded'} />
            {formData.portfolio_link && <RR l="Portfolio" v={formData.portfolio_link} />}
            {recording && <RR l="Voice Recording" v={`${fmtTime(getDur())} recorded ✓`} />}
            {formData.anything_else && <RR l="Additional Info" v={formData.anything_else} />}
          </div>
          <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-lg p-4"><div className="flex items-start"><AlertCircle className="h-5 w-5 text-amber-600 mr-2 mt-0.5" /><div><p className="font-bold text-amber-800">Ready to Submit</p><p className="text-amber-700 text-sm">Review your information before submitting.</p></div></div></div>
          <div className="flex justify-between pt-4">
            <button type="button" onClick={() => setCurrentStep(4)} className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-3 px-8 rounded-lg">← Back</button>
            <button type="button" onClick={handleSubmit} disabled={loading} className="bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-8 rounded-lg disabled:opacity-50 flex items-center gap-2">{loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Submitting...</> : 'Submit Application'}</button>
          </div>
        </div>
      )}
    </>
  );

  if (fetchingCategory || checkingSession) return <div className="text-center py-12"><Loader2 className="h-8 w-8 animate-spin text-orange-500 mx-auto mb-4" /><p className="text-gray-600">Loading...</p></div>;

  if (accountStatus === 'verify-email') return (
    <>
      <Link href={`/career/${slug}`} className="flex items-center text-indigo-600 mb-6 hover:text-orange-500"><ArrowLeft size={18} className="mr-2" /> Back</Link>
      <div className="text-center py-12">
        <div className="h-20 w-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6"><Mail className="h-10 w-10 text-blue-600" /></div>
        <h3 className="text-xl font-bold text-gray-900 mb-3">Check Your Email</h3>
        <p className="text-gray-600 mb-4">We{"'"}ve sent a confirmation link to<br /><strong>{formData.email}</strong></p>
        <p className="text-gray-500 text-sm mb-8">Click the link in the email to confirm and continue.</p>
        <div className="flex gap-3 justify-center">
          <button onClick={() => { setAccountStatus('creating'); setError(''); }} className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-3 px-6 rounded-lg">← Go Back</button>
          <Link href={`/career/${slug}`} className="bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-6 rounded-lg">Back to Job Details</Link>
        </div>
      </div>
    </>
  );

  if (submitted) return (
    <div className="text-center py-8">
      <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"><svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg></div>
      <h2 className="text-3xl font-bold text-green-600 mb-4">🎉 Application Submitted!</h2>
      <p className="text-gray-700 mb-4">
        Your application for <strong>{jobTitle}</strong> has been received.
      </p>
      <p className="text-gray-600 mb-8">
        Our HR team will review and contact you if your profile matches.
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <button onClick={() => { setSubmitted(''); openBrowseJobs(); }} className="bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-8 rounded-lg">Browse More Jobs</button>
        <Link href="/" className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-8 rounded-lg text-center">Back to Home</Link>
      </div>
    </div>
  );

  // Toast renderer. Fixed at the top-center of the viewport so it's
  // impossible to miss. We render it ABOVE the main component output by
  // returning a fragment that wraps the original output with the toast
  // node.
  const toastNode = toast ? (
    <div
      role="status"
      aria-live="polite"
      className="fixed top-4 left-1/2 -translate-x-1/2 z-[60] w-[min(92vw,520px)] animate-[slide-down_0.25s_ease-out]"
    >
      <div className="bg-white border border-blue-200 shadow-lg rounded-xl px-4 py-3 flex items-start gap-3">
        <div className="h-9 w-9 flex-shrink-0 rounded-full bg-blue-100 flex items-center justify-center">
          <AlertCircle className="h-5 w-5 text-blue-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900">Account already exists</p>
          <p className="text-sm text-gray-600 mt-0.5 break-words">{toast.message}</p>
          {toast.actionHref && toast.actionLabel && (
            <Link
              href={toast.actionHref}
              className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-orange-600 hover:text-orange-700"
            >
              {toast.actionLabel} <ChevronRight size={14} />
            </Link>
          )}
        </div>
        <button
          type="button"
          onClick={() => setToast(null)}
          aria-label="Dismiss notification"
          className="text-gray-400 hover:text-gray-600 p-1 -m-1"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  ) : null;

  // ===== LOGGED IN: DASHBOARD LAYOUT =====
  if (isLoggedIn) {
    return (
      <>
        {toastNode}
        <div className="flex flex-col lg:flex-row gap-6 min-h-[600px]">
        <aside className={`lg:w-64 flex-shrink-0 ${sidebarOpen ? 'block' : 'hidden lg:block'}`}>
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sticky top-4">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
              <div className="h-12 w-12 bg-orange-100 rounded-full flex items-center justify-center"><span className="text-orange-600 font-bold text-lg">{userName.charAt(0).toUpperCase()}</span></div>
              <div className="min-w-0"><p className="font-semibold text-gray-900 truncate">{userName}</p><p className="text-xs text-gray-500 truncate">{userEmail}</p></div>
            </div>
            <nav className="space-y-1">
              <button onClick={() => { setSidebarView('account'); setError(''); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${sidebarView === 'account' ? 'bg-orange-50 text-orange-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}>
                <User size={18} /> My Account
              </button>
              <button onClick={openBrowseJobs} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${sidebarView === 'browse' ? 'bg-orange-50 text-orange-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}>
                <Briefcase size={18} /> Browse Jobs
              </button>
              <button onClick={() => { setSidebarView('apply'); setError(''); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${sidebarView === 'apply' ? 'bg-orange-50 text-orange-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}>
                <FileText size={18} /> {alreadyApplied ? 'Application Status' : monthLimitedJobTitle ? 'Monthly Limit' : `Apply for ${jobTitle.length > 15 ? jobTitle.slice(0, 15) + '…' : jobTitle}`}
              </button>
            </nav>
            <div className="mt-6 pt-4 border-t border-gray-100">
              <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition-colors"><LogOut size={18} /> Sign Out</button>
            </div>
          </div>
        </aside>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden fixed bottom-4 right-4 z-50 bg-orange-500 text-white p-4 rounded-full shadow-lg" aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}>
          {sidebarOpen ? <X size={24} /> : <User size={24} />}
        </button>
        <div className="flex-1 min-w-0">
          {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center"><AlertCircle className="h-5 w-5 mr-2" /><span>{error}</span></div>}

          {sidebarView === 'account' && (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h2 className="text-xl font-bold text-[#002F4A] mb-6">My Account</h2>
              {editSuccess && <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg mb-4">{editSuccess}</div>}
              <div className="space-y-4">
                <div><label className="block text-gray-700 font-medium mb-1">Full Name</label><input type="text" name="name" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" value={formData.name} onChange={handleInputChange} /></div>
                <div><label className="block text-gray-700 font-medium mb-1">Email</label><input type="email" value={userEmail} disabled className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-500" /></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><label className="block text-gray-700 font-medium mb-1">Phone</label><input type="tel" name="phone" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" value={formData.phone} onChange={handleInputChange} /></div>
                  <div><label className="block text-gray-700 font-medium mb-1">Gender</label><select name="gender" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" value={formData.gender} onChange={handleInputChange}><option value="">Select</option><option value="male">Male</option><option value="female">Female</option></select></div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><label className="block text-gray-700 font-medium mb-1">Country</label><input type="text" name="country" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" value={formData.country} onChange={handleInputChange} /></div>
                  <div><label className="block text-gray-700 font-medium mb-1">City</label><input type="text" name="city" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" value={formData.city} onChange={handleInputChange} /></div>
                </div>
                <div><label className="block text-gray-700 font-medium mb-1">Preferred Languages</label>
                  <div className="grid grid-cols-3 gap-2">
                    {['English', 'Amharic', 'Arabic'].map(lang => (
                      <label key={lang} className={`flex items-center gap-2 p-2 border rounded-lg cursor-pointer transition-colors ${safeLanguage.includes(lang) ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-200 hover:border-gray-300'}`}>
                        <input type="checkbox" checked={safeLanguage.includes(lang)} onChange={() => handleLanguageToggle(lang)} className="w-4 h-4 text-orange-500 rounded" />
                        <span className="text-sm">{lang}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="pt-4"><button onClick={handleUpdateAccount} disabled={editLoading} className="bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-8 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2">{editLoading ? <><Loader2 className="h-5 w-5 animate-spin" /> Saving...</> : 'Save Changes'}</button></div>
              </div>
            </div>
          )}

          {sidebarView === 'browse' && (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h2 className="text-xl font-bold text-[#002F4A] mb-6">Browse Jobs</h2>
              {loadingJobs ? (
                <div className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin text-orange-500 mx-auto mb-2" /><p className="text-gray-500 text-sm">Loading jobs...</p></div>
              ) : allJobs.length === 0 ? (
                <div className="text-center py-8"><Briefcase className="h-12 w-12 text-gray-300 mx-auto mb-3" /><p className="text-gray-500">No open positions at the moment.</p></div>
              ) : (
                <div className="space-y-4">
                  {allJobs.map((job: any) => {
                    const app = userApplications.find((a: any) => a.job_id === job.id);
                    return (
                      <div key={job.id} className="border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-0.5 rounded-full">{job.category}</span>
                              {app && <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_COLORS[app.status] || 'bg-gray-100 text-gray-800'}`}>{app.status}</span>}
                            </div>
                            <h3 className="font-bold text-[#002F4A] text-lg">{job.title}</h3>
                            <div className="flex flex-wrap items-center gap-3 text-gray-500 text-sm mt-1">
                              <span className="flex items-center gap-1"><MapPin size={14} /> {job.location || job.work_model}</span>
                              <span className="flex items-center gap-1"><Clock size={14} /> {job.type}</span>
                              <span className="flex items-center gap-1"><CalendarClock size={14} /> Closes: {new Date(job.closes).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                            </div>
                          </div>
                          <div className="flex-shrink-0">
                            {app ? (
                              // The "edit application" flow has been removed —
                              // for already-applied jobs we simply show the
                              // status badge above; the "Apply Now" CTA is
                              // only rendered for jobs the user has not
                              // applied to.
                              <span className="text-xs text-gray-400 font-medium">Already Applied</span>
                            ) : job.slug === slug ? (
                              // The user is already viewing the apply page
                              // for this job (it would be the URL they're
                              // currently on). Don't render an "Apply Now"
                              // link here, since clicking it would just
                              // re-render the same page and look like a
                              // no-op reload. Show a clear "Viewing now"
                              // indicator instead.
                              <span className="inline-flex items-center gap-1 text-xs text-orange-600 font-semibold">
                                Viewing now <ChevronRight size={14} />
                              </span>
                            ) : monthLimitedJobTitle ? (
                              // This month's single application was already used
                              // on another posting, so every remaining job stays
                              // visible but not apply-able until the 1st.
                              <span
                                className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-lg bg-amber-50 text-amber-700 border border-amber-200"
                                title={`You already applied for ${monthLimitedJobTitle} this month. You can apply again from ${monthReopenLabel}.`}
                              >
                                <CalendarClock size={14} /> Locked this month
                              </span>
                            ) : (
                              <Link
                                href={`/career/${job.slug}/apply`}
                                className="inline-flex items-center gap-1 bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2 px-5 rounded-lg text-sm transition-colors"
                              >
                                Apply Now <ChevronRight size={16} />
                              </Link>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {sidebarView === 'apply' && (alreadyApplied ? alreadyAppliedCard : monthLimitedJobTitle ? monthLockedCard : applyContent)}
        </div>
      </div>
      </>
    );
  }

  // When not logged in, `alreadyApplied` is never set, so this safely
  // returns the regular form. We still render the toast above the form so
  // the "User already registered" notification is visible during the
  // unauthenticated signup flow (which is the primary use case here).
  return (
    <>
      {toastNode}
      {alreadyApplied ? alreadyAppliedCard : monthLimitedJobTitle ? monthLockedCard : applyContent}
    </>
  );
}

function RR({ l, v }: { l: string; v: string }) {
  return <div><dt className="text-sm font-medium text-gray-500">{l}</dt><dd className="text-sm text-gray-900 mt-1 whitespace-pre-wrap">{v}</dd></div>;
}
